import React, { lazy, Suspense, memo, useMemo } from 'react';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import imageUrlBuilder from '@sanity/image-url';
import { config } from '../../utils/config';

// ----------------------------------------------------------------------
// 1. Validate config and set up image builder
// ----------------------------------------------------------------------
if (!config.sanity?.projectId) {
  throw new Error('Missing Sanity project ID in config');
}

const builder = imageUrlBuilder({
  projectId: config.sanity.projectId,
  dataset: 'production', // fallback to 'production'
});

const urlFor = (source: any) => builder.image(source);

// ----------------------------------------------------------------------
// 2. Lazy load syntax highlighter (optional)
// ----------------------------------------------------------------------
const SyntaxHighlighter = lazy(() =>
  import('react-syntax-highlighter').then((mod) => ({ default: mod.Prism }))
);

// ----------------------------------------------------------------------
// 3. Custom components (all memoized)
// ----------------------------------------------------------------------

// Helper to preserve line breaks (if still needed)
const TextWithBreaks = memo(({ children }: { children: React.ReactNode }) => {
  const nodes = React.Children.toArray(children).flatMap((child, index) => {
    if (typeof child === 'string') {
      // Replace newlines with <br />
      return child.split('\n').flatMap((text, i) =>
        i === 0 ? [text] : [<br key={`br-${index}-${i}`} />, text]
      );
    }
    if (React.isValidElement(child) && child.props.children) {
      // Recursively process children
      return React.cloneElement(child, {
        ...child.props,
        children: <TextWithBreaks>{child.props.children}</TextWithBreaks>,
      });
    }
    return child;
  });
  return <>{nodes}</>;
});

// Image component with Sanity image URL builder
const ImageComponent = memo(({ value }: { value: any }) => {
  const { asset, alt, caption } = value;

  // Build optimized image URL (width, format, etc.)
  const imageUrl = useMemo(
    () =>
      urlFor(asset)
        .width(1200)
        .fit('max')
        .auto('format')
        .url(),
    [asset]
  );

  return (
    <figure className="my-4">
      <img
        src={imageUrl}
        alt={alt || ''}
        loading="lazy"
        className="mx-auto rounded-lg"
      />
      {caption && (
        <figcaption className="text-center text-sm mt-2 text-gray-600">
          {caption}
        </figcaption>
      )}
    </figure>
  );
});

// Code block with lazy-loaded syntax highlighter
const CodeComponent = memo(({ value }: { value: any }) => {
  const { language, code } = value;
  return (
    <Suspense fallback={<pre className="bg-gray-100 p-4 rounded">Loading code…</pre>}>
      <SyntaxHighlighter language={language || 'text'} className="rounded my-4">
        {code}
      </SyntaxHighlighter>
    </Suspense>
  );
});

// Block styles (headings, paragraphs, blockquotes)
const BlockComponent = memo(({ children, blockStyle, className }: any) => {
  const textWithBreaks = <TextWithBreaks>{children}</TextWithBreaks>;
  switch (blockStyle) {
    case 'h1': return <h1 className={className}>{textWithBreaks}</h1>;
    case 'h2': return <h2 className={className}>{textWithBreaks}</h2>;
    case 'h3': return <h3 className={className}>{textWithBreaks}</h3>;
    case 'h4': return <h4 className={className}>{textWithBreaks}</h4>;
    case 'h5': return <h5 className={className}>{textWithBreaks}</h5>;
    case 'h6': return <h6 className={className}>{textWithBreaks}</h6>;
    case 'blockquote': return <blockquote className={className}>{textWithBreaks}</blockquote>;
    default: return <p className={className}>{textWithBreaks}</p>;
  }
});

// List items
const ListItemComponent = memo(({ children }: any) => (
  <li className="">{children}</li>
));

// Lists (bullet / number)
const ListComponent = memo(({ value, children }: any) => {
  const { listItem } = value;
  return listItem === 'bullet' ? (
    <ul className="list-disc list-outside text-blue-700 py-2 pl-[2rem]">{children}</ul>
  ) : (
    <ol className="list-decimal list-outside py-2 pl-[2rem]">{children}</ol>
  );
});

// Marks (inline formatting)
const MarksComponent = {
  strong: ({ children }: any) => <strong className="font-bold mt-3 mb-1">{children}</strong>,
  em: ({ children }: any) => <em className="italic">{children}</em>,
  underline: ({ children }: any) => <u>{children}</u>,
  strike: ({ children }: any) => <s>{children}</s>,
  code: ({ children }: any) => <code className="bg-gray-100 px-1 rounded">{children}</code>,
  link: ({ value, children }: any) => {
    const href = value?.href || '';
    const target = href.startsWith('http') ? '_blank' : undefined;
    return (
      <a href={href} target={target} rel="noopener noreferrer" className="text-blue-600 underline">
        {children}
      </a>
    );
  },
};

// ----------------------------------------------------------------------
// 4. Assemble the components object for PortableText
// ----------------------------------------------------------------------
const components: PortableTextComponents = {
  block: {
    normal: ({ children }) => <BlockComponent blockStyle="normal">{children}</BlockComponent>,
    h1: ({ children }) => <BlockComponent blockStyle="h1">{children}</BlockComponent>,
    h2: ({ children }) => <BlockComponent blockStyle="h2">{children}</BlockComponent>,
    h3: ({ children }) => <BlockComponent blockStyle="h3">{children}</BlockComponent>,
    h4: ({ children }) => <BlockComponent blockStyle="h4">{children}</BlockComponent>,
    h5: ({ children }) => <BlockComponent blockStyle="h5">{children}</BlockComponent>,
    h6: ({ children }) => <BlockComponent blockStyle="h6">{children}</BlockComponent>,
    blockquote: ({ children }) => <BlockComponent blockStyle="blockquote">{children}</BlockComponent>,
    // Custom alignment styles (these only need className)
    left: ({ children }) => <BlockComponent className="text-left">{children}</BlockComponent>,
    center: ({ children }) => <BlockComponent className="text-center">{children}</BlockComponent>,
    right: ({ children }) => <BlockComponent className="text-right">{children}</BlockComponent>,
    justify: ({ children }) => <BlockComponent className="text-justify">{children}</BlockComponent>,
  },
  list: {
    bullet: ({ children }) => <ListComponent value={{ listItem: 'bullet' }}>{children}</ListComponent>,
    number: ({ children }) => <ListComponent value={{ listItem: 'number' }}>{children}</ListComponent>,
  },
  listItem: {
    bullet: ({ children }) => <ListItemComponent>{children}</ListItemComponent>,
    number: ({ children }) => <ListItemComponent>{children}</ListItemComponent>,
  },
  marks: MarksComponent,
  types: {
    image: ImageComponent,
    code: CodeComponent,
    // Add other custom types here (e.g., callout, embed, etc.)
  },
  hardBreak: () => <br />,
};

// ----------------------------------------------------------------------
// 5. Main component
// ----------------------------------------------------------------------
export const BlockText = memo(({ blocks }: { blocks: any[] }) => {
  return <PortableText value={blocks} components={components} />;
});